import { useQuery } from '@tanstack/react-query';
import * as C from '../../../../components/ui/common/CommonStyles';
import TableUnit from '../../../../components/units/Edgecross/Brochure/TableUnit';
import { AxiosError } from 'axios';
import { AxiosCommonError } from '../../../../shared/types';
import { handleError } from '../../../../components/ui/err/handleError';
import { useSearchParams } from 'react-router-dom';
import { Flex, Group, Pagination, Select } from '@mantine/core';
import { cleanParams } from '../../../../shared/utils/cleanParams';
import CustomSearch from '../../../../components/ui/search/customSearch';
import { useCustomSearch } from '../../../../shared/hook/useCustomSearch';
import { operationApis } from '../../../../shared/apis/operation/apis';

const solutions = [
  {
    value: 'all',
    label: '전체',
  },
  {
    value: 'machine-gpt',
    label: 'Machine GPT',
  },
  {
    value: 'machine-manager-solution',
    label: '머신 매니저',
  },
  {
    value: 'haccp-manager-solution',
    label: 'HACCP 매니저',
  },
  {
    value: 'relay-point',
    label: '릴레이 포인트',
  },
  {
    value: 'v-ola',
    label: 'V-OLA',
  },
];

const options = [
  { value: 'name', label: '고객명' },
  { value: 'company', label: '회사명' },
];

export default function BrochureRequest() {
  const initSearchType = 'name';
  // 클라이언트단 url parameter 설정
  const [searchParams, setSearchParams] = useSearchParams('');
  const searchWord = searchParams.get('keyword') || '';
  const searchType = searchParams.get('searchType') || initSearchType;
  const filterValue = searchParams.get('solution') || 'all';
  const page = searchParams.get('page') || '1';
  const size = searchParams.get('size') || '10';

  const { inputRef, handleSearch, resetWord, handleSearchType, resetSearchInput } = useCustomSearch({
    initType: initSearchType,
  });

  // 제품 소개서 신청 리스트 조회
  const { data: solutionRequestList } = useQuery(
    ['getSolutionRequestList', Number(page), Number(size), searchType, searchWord, filterValue],
    () =>
      operationApis.getSolutionRequestList({
        page: Number(page),
        size: Number(size),
        filterField: 'solution',
        filterValue: filterValue === 'all' ? '' : filterValue,
        searchField: searchType,
        searchValue: searchWord,
        isAsc: false,
        sortBy: 'id',
      }),
    {
      onError: (error: AxiosError<AxiosCommonError>) => {
        handleError(error);
      },
    },
  );

  // 페이지네이션 처리를 위한 토탈값
  const total: number = solutionRequestList?.data.totalCount;

  const onChangeFilter = (option: { label: string; value: string }) => {
    if (!option) return;
    const currentParams = Object.fromEntries(searchParams.entries());
    setSearchParams(
      cleanParams({
        ...currentParams,
        page: '1',
        solution: option.value,
      }),
    );
  };

  return (
    <div>
      <C.TitleBox>
        <div className="title">제품 소개서 신청 현황</div>
      </C.TitleBox>
      <C.ListBox>
        <C.Toolbar>
          <Flex align="flex-end" gap="xs">
            <Select
              w={140}
              label="솔루션"
              data={solutions}
              value={filterValue}
              onChange={(_value, option) => onChangeFilter(option)}
            />
            <CustomSearch
              resetSearch={resetWord}
              resetSearchInput={resetSearchInput}
              searchType={searchType}
              setSearchType={handleSearchType}
              inputRef={inputRef}
              handleSearch={handleSearch}
              onClickSearch={(e: React.MouseEvent<HTMLButtonElement>) => {
                handleSearch(e);
              }}
              searchOptions={options}
            />
          </Flex>
        </C.Toolbar>
        <C.TableWrap>
          <TableUnit data={solutionRequestList} page={Number(page)} size={Number(size)} />
          {total > 0 ? (
            <Group justify="center" my="md">
              <Pagination
                total={Math.ceil(total / Number(size))}
                value={Number(page)}
                onChange={(value) => {
                  const currentParams = Object.fromEntries(searchParams.entries());
                  setSearchParams(
                    cleanParams({
                      ...currentParams,
                      page: String(value),
                    }),
                  );
                }}
              />
            </Group>
          ) : null}
        </C.TableWrap>
      </C.ListBox>
    </div>
  );
}
